import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import aflPlayerData from "../../assets/afl_player_data_2026.json";

// Team logos
import AdlLogo from "../../assets/team_logos/adl.svg";
import BlLogo from "../../assets/team_logos/bl.svg";
import CarlLogo from "../../assets/team_logos/carl.svg";
import CollLogo from "../../assets/team_logos/coll.svg";
import EssLogo from "../../assets/team_logos/ess.svg";
import FreLogo from "../../assets/team_logos/fre.svg";
import GeelLogo from "../../assets/team_logos/geel.svg";
import GcLogo from "../../assets/team_logos/gc.svg";
import GwsLogo from "../../assets/team_logos/gws.svg";
import HawLogo from "../../assets/team_logos/haw.svg";
import MelbLogo from "../../assets/team_logos/melb.svg";
import NmLogo from "../../assets/team_logos/nm.svg";
import PortLogo from "../../assets/team_logos/port.svg";
import RichLogo from "../../assets/team_logos/rich.svg";
import StkLogo from "../../assets/team_logos/stk.svg";
import SydLogo from "../../assets/team_logos/syd.svg";
import WceLogo from "../../assets/team_logos/wce.svg";
import WbdLogo from "../../assets/team_logos/wbd.svg";

// Marks the leading side of a matchup.
const Crown = ({ x, y, fill }) => (
  <g transform={`translate(${x - 7}, ${y - 15})`} style={{ pointerEvents: 'none' }}>
    <path d="M0 10 L0 1.5 L3.5 5 L7 0 L10.5 5 L14 1.5 L14 10 Z" fill={fill} opacity="0.95" />
  </g>
);

const BYOComparisonChart = ({ groupAPlayers = [], groupBPlayers = [], selectedTeam = null, voteData, getEnabledKeys, isTeamComparison = false }) => {
  // Display order for charts (C1..C5 then external)
  const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
  const enabledSourceKeys = getEnabledKeys();
  const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledSourceKeys).has(k));
  const keyToLabel = { 
    count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
    betfair: 'Betfair', espn: 'ESPN', byb: 'BYB' 
  };

  // Calculate combined totals for each group
  const calculateGroupTotals = (players) => {
    if (!voteData?.leaderboard || players.length === 0) return {};
    
    const totals = {};
    enabledDisplayKeys.forEach(key => {
      totals[key] = 0;
    });

    players.forEach(selectedPlayer => {
      const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
      if (playerData) {
        enabledDisplayKeys.forEach(key => {
          if (playerData[key] !== undefined) {
            totals[key] += playerData[key];
          }
        });
      }
    });

    return totals;
  };

  const groupATotals = calculateGroupTotals(groupAPlayers);
  const groupBTotals = isTeamComparison && selectedTeam ? calculateTeamTotals(selectedTeam) : calculateGroupTotals(groupBPlayers);

  // Calculate team totals for players-vs-team bet type
  function calculateTeamTotals(teamName) {
    if (!voteData?.leaderboard) return {};
    
    const totals = {};
    enabledDisplayKeys.forEach(key => {
      totals[key] = 0;
    });

    voteData.leaderboard.forEach(player => {
      const playerTeam = player.full_team_name || player.team;
      if (playerTeam === teamName) {
        enabledDisplayKeys.forEach(key => {
          if (player[key] !== undefined) {
            totals[key] += player[key];
          }
        });
      }
    });

    return totals;
  }
  

  // Get all values for range calculation
  const allValues = [];
  enabledDisplayKeys.forEach(key => {
    if (groupATotals[key] !== undefined) allValues.push(groupATotals[key]);
    if (groupBTotals[key] !== undefined) allValues.push(groupBTotals[key]);
  });

  const hasData = (groupAPlayers.length > 0 || groupBPlayers.length > 0 || (isTeamComparison && selectedTeam)) && enabledDisplayKeys.length > 0;
  
  // Force some default values if no actual data
  let maxCount, minCount;
  if (hasData && allValues.length > 0) {
    maxCount = Math.max(...allValues, 1);
    minCount = Math.min(...allValues, 0);
  } else if (hasData) {
    // Players selected but no vote data - use defaults
    maxCount = 10;
    minCount = 0;
  } else {
    maxCount = 30;
    minCount = 0;
  }
  
  // Calculate range for better visualization with some padding
  const padding = (maxCount - minCount) * 0.1 || 1;
  const visualMin = Math.floor(minCount - padding);
  const visualMax = Math.ceil(maxCount + padding);
  
  // Generate y-axis labels
  const generateYAxisLabels = () => {
    const labels = [];
    const range = visualMax - visualMin;
    const step = range > 10 ? Math.ceil(range / 5) : 1;
    
    for (let i = visualMin; i <= visualMax; i += step) {
      if (i >= 0) labels.push(i);
    }
    return labels;
  };
  
  const yAxisLabels = generateYAxisLabels();

  // Function to get player image
  const getPlayerImage = (playerName) => {
    const player = aflPlayerData.find(p => p.player_name === playerName);
    return player ? player.image_src : null;
  };

  // Function to get team logo
  const getTeamLogo = (teamName) => {
    const logos = {
      "Adelaide_Crows": AdlLogo,
      "Brisbane_Lions": BlLogo,
      "Carlton": CarlLogo,
      "Collingwood": CollLogo,
      "Essendon": EssLogo,
      "Fremantle": FreLogo,
      "Geelong_Cats": GeelLogo,
      "Gold_Coast_Suns": GcLogo,
      "GWS_Giants": GwsLogo,
      "Hawthorn": HawLogo,
      "Melbourne": MelbLogo,
      "North_Melbourne": NmLogo,
      "Port_Adelaide": PortLogo,
      "Richmond": RichLogo,
      "St_Kilda": StkLogo,
      "Sydney_Swans": SydLogo,
      "West_Coast_Eagles": WceLogo,
      "Western_Bulldogs": WbdLogo,
    };
    return logos[teamName] || null;
  };

  // Calculate bar heights directly without animation
  const getBarHeight = (value, groupPlayers, isTeamBar = false) => {
    if (!hasData) return 0;
    
    // For team comparison, don't check groupPlayers.length for team bars
    if (!isTeamBar && groupPlayers.length === 0) return 0;
    
    const height = visualMax > visualMin ? ((value - visualMin) / (visualMax - visualMin)) * 100 : 0;
    
    // Ensure minimum height for visibility when players are selected or team is selected
    return value > 0 ? Math.max(height, 5) : Math.max(height, 2);
  };

  // Show empty chart structure when no players are selected
  const showEmptyChart = !hasData;

  const showGroupA = groupAPlayers.length > 0 || (isTeamComparison && groupAPlayers.length > 0);
  const showGroupB = groupBPlayers.length > 0 || (isTeamComparison && selectedTeam);

  // Measure the container so the SVG always spans the available space.
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 560, h: 260 });
  const [hover, setHover] = useState(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () =>
      setSize({ w: Math.max(260, el.clientWidth), h: Math.max(180, el.clientHeight) });
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const geom = useMemo(() => {
    const padL = 34;
    const padR = 12;
    const padT = 16;
    const padB = 26;
    const n = enabledDisplayKeys.length;

    const svgW = size.w;
    const svgH = size.h;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;
    const slot = n > 0 ? innerW / n : innerW;

    const pairCount = (showGroupA ? 1 : 0) + (showGroupB ? 1 : 0);
    const gap = pairCount > 1 ? Math.min(6, slot * 0.08) : 0;
    const barW = pairCount > 0 ? Math.min((slot * 0.68 - gap) / pairCount, 34) : 0;

    const baselineY = padT + innerH;
    const yFor = (v) => baselineY - ((v - visualMin) / (visualMax - visualMin || 1)) * innerH;
    const xFor = (i) => padL + slot * i + slot / 2;

    return { padL, padR, padT, padB, svgW, svgH, innerW, innerH, slot, gap, barW, pairCount, baselineY, yFor, xFor };
  }, [enabledDisplayKeys.length, size, visualMin, visualMax, showGroupA, showGroupB]);

  const { padL, padR, padT, padB, svgW, svgH, innerW, innerH, slot, gap, barW, pairCount, baselineY, xFor } = geom;

  // Match the previous breakpoint behaviour: value labels, crowns and the team
  // badge only appear once there is room for them.
  const showBarDetail = svgW >= 480;

  // Shorten Betfair to BF when the chart is narrow (mobile widths)
  const getDisplayLabel = (label) => (label === "Betfair" && svgW < 480 ? "BF" : label);

  const animKey = `${enabledDisplayKeys.join(',')}|${groupAPlayers.length}|${groupBPlayers.length}|${selectedTeam || ''}`;

  // Converts the percentage heights above into pixels within the plot area.
  const pxFor = (pct) => (pct / 100) * innerH;

  const teamLogo = isTeamComparison && selectedTeam ? getTeamLogo(selectedTeam) : null;

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3 w-full h-full flex flex-col min-h-0">
      <style>{`
        @keyframes byoCmpBarGrow { from { transform: scaleY(0.001); } to { transform: scaleY(1); } }
        .byo-cmp-bar-grow { transform-box: fill-box; transform-origin: 50% 100%; animation: byoCmpBarGrow 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
      `}</style>

      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">
          Vote Visualization
        </div>
        {!showEmptyChart && (
          <div className="flex items-center gap-3">
            {showGroupA && (
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-400">
                  {isTeamComparison ? 'Players' : 'Group A'}
                </span>
              </div>
            )}
            {showGroupB && (
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                <span className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-400">
                  {isTeamComparison && selectedTeam ? 'Team' : 'Group B'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div ref={wrapRef} className="relative w-full flex-1 min-h-[220px] sm:min-h-[260px] lg:min-h-0">
        <svg width={svgW} height={svgH} className="block" role="img" aria-label="Group comparison by dataset">
          <defs>
            <linearGradient id="byoCmpA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="byoCmpB" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#38bdf8" />
              <stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
          </defs>

          {/* Gridlines + y labels */}
          {yAxisLabels.map((label) => {
            const y = geom.yFor(label);
            return (
              <g key={`grid-${label}`}>
                <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#1e293b" strokeWidth="1" />
                <text
                  x={padL - 6}
                  y={y + 3}
                  textAnchor="end"
                  className="fill-slate-500"
                  style={{ fontSize: 9, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', fontVariantNumeric: 'tabular-nums' }}
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* Separators between dataset groups */}
          {enabledDisplayKeys.length > 1 && enabledDisplayKeys.slice(0, -1).map((_, index) => {
            const x = padL + innerW * ((index + 1) / enabledDisplayKeys.length);
            return (
              <line
                key={`separator-${index}`}
                x1={x}
                y1={padT}
                x2={x}
                y2={baselineY}
                stroke="#334155"
                strokeWidth="1"
                strokeDasharray="3 4"
                opacity="0.7"
              />
            );
          })}

          {/* Bars, grouped by dataset */}
          {!showEmptyChart && (
            <g key={animKey}>
              {enabledDisplayKeys.map((key, countIndex) => {
                const cx = xFor(countIndex);
                const aValue = groupATotals[key] || 0;
                const bValue = groupBTotals[key] || 0;
                const maxValue = Math.max(aValue, bValue);
                const aWins = aValue >= maxValue && maxValue > 0;
                const bWins = bValue >= maxValue && maxValue > 0;
                const dim = hover != null && hover !== countIndex;

                // Lay the visible bars out side by side, centred in the slot.
                const span = pairCount * barW + (pairCount - 1) * gap;
                let cursor = cx - span / 2;
                const slots = {};
                if (showGroupA) {
                  slots.a = cursor;
                  cursor += barW + gap;
                }
                if (showGroupB) {
                  slots.b = cursor;
                }

                const renderBar = (xLeft, value, groupPlayers, isTeamBar, fill, isWinner, crownFill, label) => {
                  const h = Math.max(pxFor(getBarHeight(value, groupPlayers, isTeamBar)), 10);
                  const y = baselineY - h;
                  const inside = h >= 16;
                  return (
                    <>
                      <rect
                        x={xLeft}
                        y={y}
                        width={barW}
                        height={h}
                        rx={3}
                        fill={fill}
                        opacity={dim ? 0.45 : 1}
                        className="byo-cmp-bar-grow"
                        style={{ transition: 'opacity 0.12s', animationDelay: `${Math.min(countIndex * 40, 500)}ms` }}
                      >
                        <title>{`${label}: ${value.toFixed(2)}`}</title>
                      </rect>
                      {showBarDetail && (
                        <text
                          x={xLeft + barW / 2}
                          y={inside ? y + 12 : y - 4}
                          textAnchor="middle"
                          className={inside ? 'fill-slate-950' : 'fill-slate-200'}
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                            fontVariantNumeric: 'tabular-nums',
                            pointerEvents: 'none',
                            ...(inside
                              ? { paintOrder: 'stroke', stroke: 'rgba(255,255,255,0.35)', strokeWidth: 2, strokeLinejoin: 'round' }
                              : null),
                          }}
                          opacity={dim ? 0.5 : 1}
                        >
                          {value.toFixed(2)}
                        </text>
                      )}
                      {showBarDetail && isWinner && (
                        <Crown x={xLeft + barW / 2} y={inside ? y - 4 : y - 16} fill={crownFill} />
                      )}
                    </>
                  );
                };

                return (
                  <g
                    key={key}
                    onMouseEnter={() => setHover(countIndex)}
                    onMouseLeave={() => setHover((prev) => (prev === countIndex ? null : prev))}
                    style={{ cursor: 'default' }}
                  >
                    {/* Hover hit area */}
                    <rect x={cx - slot / 2} y={padT} width={slot} height={svgH - padT - padB} fill="transparent" />

                    {showGroupA &&
                      renderBar(
                        slots.a,
                        aValue,
                        groupAPlayers,
                        false,
                        'url(#byoCmpA)',
                        aWins,
                        '#fbbf24',
                        isTeamComparison ? 'Players' : 'Group A'
                      )}

                    {showGroupB && (
                      <>
                        {renderBar(
                          slots.b,
                          bValue,
                          groupBPlayers,
                          isTeamComparison,
                          'url(#byoCmpB)',
                          bWins,
                          '#fbbf24',
                          isTeamComparison && selectedTeam ? selectedTeam.replace(/_/g, ' ') : 'Group B'
                        )}
                        {/* Team badge sits at the foot of the team bar */}
                        {showBarDetail && teamLogo && (
                          <image
                            href={teamLogo}
                            x={slots.b + barW / 2 - 9}
                            y={baselineY - 24}
                            width={18}
                            height={18}
                            opacity={dim ? 0.5 : 0.9}
                            style={{ pointerEvents: 'none' }}
                          />
                        )}
                      </>
                    )}

                    {/* Dataset label */}
                    <text
                      x={cx}
                      y={svgH - 8}
                      textAnchor="middle"
                      className={hover === countIndex ? 'fill-slate-300' : 'fill-slate-500'}
                      style={{
                        fontSize: 9,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {getDisplayLabel(keyToLabel[key])}
                    </text>
                  </g>
                );
              })}
            </g>
          )}

          {/* Baseline */}
          <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
        </svg>

        {/* Empty state message */}
        {showEmptyChart && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg border border-dashed border-slate-800/80 bg-[#0a1120]/40">
            <div className="text-slate-500 text-xs font-mono text-center px-2">
              <div>No data to display</div>
              <div className="text-[10px] mt-1 text-slate-600 uppercase tracking-[0.12em]">
                Select players for both groups to view comparison
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compact group counts under the chart */}
      {!showEmptyChart && (
        <div className="mt-2 pt-2 border-t border-slate-800/70 flex-shrink-0">
          <div className="flex flex-wrap gap-2 justify-center">
            {groupAPlayers.length > 0 && (
              <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/25 rounded-md px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-emerald-300 text-[10px] font-mono uppercase tracking-[0.1em]">
                  {isTeamComparison ? 'Players' : 'Group A'} · {groupAPlayers.length}
                </span>
              </div>
            )}
            {(groupBPlayers.length > 0 || (isTeamComparison && selectedTeam)) && (
              <div className="flex items-center gap-1.5 bg-sky-500/10 border border-sky-500/25 rounded-md px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                <span className="text-sky-300 text-[10px] font-mono uppercase tracking-[0.1em]">
                  {isTeamComparison && selectedTeam
                    ? selectedTeam.replace(/_/g, ' ')
                    : `Group B · ${groupBPlayers.length}`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BYOComparisonChart;
